/**
 * AchievementBadge primitive (Plan 02-14, Task 1).
 *
 * UI-SPEC §"<AchievementBadge keyName size unlocked secret />" + §9
 * Achievement gallery.
 *
 * Renders the SVG art for an achievement key inside a circular tile. v1
 * uses the existing Icon catalogue as the art set (a key→Icon mapping
 * matching each registry entry's `icon_key`). Custom badge SVGs may
 * replace the mapping later; the primitive contract stays stable.
 *
 * Three visual states — merkeskiven fra Merker-skissen (#152):
 *   - unlocked              datopanelets gradient (`bg-panel`), mørk
 *                           `text-on-panel`-kunst og en lime glorie
 *                           (`ring-lime-bg`)
 *   - locked-non-secret     stiplet `border-secondary-line`-ring uten fyll,
 *                           kunsten i ink-2 — barnet ser hva det kan få
 *   - locked-secret         no key-specific art — only Icon.Lock 24px in ink-2
 *                           on a flat `bg-track` disc (preserves surprise per
 *                           T-02-27 + UI-SPEC §9)
 *
 * Tilstanden står også i `data-state`, og et galleri viser alltid navnet ved
 * siden av skiven, så fargen er aldri eneste kjennetegn.
 *
 * Plan 15 (L4 takeover) reuses this primitive for the hero badge (192 fra #155).
 *
 * `art="glyph"` er skivene på Merker (#156, steg 6c): 52 px med terskelen som
 * tall («5», «25», «3×») i stedet for ikonet, og kronen for Ukens regent.
 * Tallet er `goal.count` fra registeret i `@stell/shared`, så det følger
 * terskelen evaluatoren bruker. Merker uten terskel (Ukens regent, de
 * hemmelige når de er låst opp) faller tilbake på ikonet.
 */
import type { ReactNode } from 'react';
import { achievementGoal } from '@stell/shared';
import { Icon } from './Icon';

/**
 * v1 art mapping. Each key maps to:
 *   - the Icon component used as the visible glyph
 *   - the data-testid suffix the gallery / takeover tests select against
 *
 * Keys mirror packages/shared/src/achievements/registry.ts ACHIEVEMENTS map.
 */
const KEY_ART: Record<
  string,
  { Icon: (props: { size?: number; className?: string }) => ReactNode }
> = {
  first_chore: { Icon: Icon.Sparkle },
  chore_5: { Icon: Icon.Check },
  chore_25: { Icon: Icon.Check },
  chore_100: { Icon: Icon.Trophy },
  streak_3: { Icon: Icon.Flame },
  streak_7: { Icon: Icon.Flame },
  streak_30: { Icon: Icon.Flame },
  weekly_winner: { Icon: Icon.Crown },
  night_owl: { Icon: Icon.Sparkle },
  early_bird: { Icon: Icon.Sparkle },
  comeback: { Icon: Icon.Sparkle },
  combo_3: { Icon: Icon.Check },
};

/** Tallet i skiven på Merker: terskelen, og «×» for samme dag, som i skissen. */
function goalGlyph(keyName: string): string | undefined {
  const goal = achievementGoal(keyName);
  if (!goal) return undefined;
  return goal.kind === 'sameDay' ? `${goal.count}×` : String(goal.count);
}

interface AchievementBadgeProps {
  /** Achievement key (e.g. 'first_chore', 'streak_7'). */
  keyName: string;
  /**
   * Pixel size for the circular tile. Default 88 (gallery cell phone size).
   * 192 er skiven i L4-feiringen (#155, Feiring-skissen), 52 skiven på Merker (#156).
   */
  size?: 52 | 88 | 128 | 192 | 240;
  /** `glyph`: terskelen som tall i skiven, som på Merker. Standard er ikonet. */
  art?: 'icon' | 'glyph';
  /** Whether the viewer's target member has unlocked this achievement. */
  unlocked: boolean;
  /** Secret achievements render as Lock-only silhouettes when locked. */
  secret: boolean;
  className?: string;
}

export function AchievementBadge({
  keyName,
  size = 88,
  unlocked,
  secret,
  art = 'icon',
  className,
}: AchievementBadgeProps): ReactNode {
  // Locked-secret: no key-specific art at all. Just a Lock glyph centred
  // on the locked-tile surface (paper-2). Preserves surprise (T-02-27).
  const isLockedSecret = !unlocked && secret;

  // Skiven per tilstand. Ringen bruker box-shadow (ring-*), så den tar ikke
  // plass og flytter ikke kunsten.
  const tileSurface = unlocked
    ? 'bg-panel text-on-panel ring-4 ring-lime-bg'
    : isLockedSecret
      ? 'bg-track text-ink-2'
      : 'border-2 border-dashed border-secondary-line text-ink-2';

  // Glyph size: keep the inner art comfortably inside the tile (≈ 50%).
  // I glyph-skiven er ikonene 24 px, som kronen i Merker-skissen.
  const innerSize = art === 'glyph' ? 24 : Math.round(size * 0.55);

  // Lock glyph for the locked-secret state — fixed 24px per UI-SPEC §9;
  // 22 px i den lille skiven på Merker.
  const lockGlyphSize = size === 52 ? 22 : 24;

  const ArtForKey = KEY_ART[keyName];
  const glyph = art === 'glyph' ? goalGlyph(keyName) : undefined;

  return (
    <div
      data-testid="achievement-badge"
      data-key={keyName}
      data-state={
        unlocked
          ? 'unlocked'
          : secret
            ? 'locked-secret'
            : 'locked-non-secret'
      }
      style={{ width: `${size}px`, height: `${size}px` }}
      className={[
        'inline-flex items-center justify-center rounded-full',
        tileSurface,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {isLockedSecret ? (
        <span
          data-testid="achievement-art-lock"
          aria-hidden="true"
        >
          <Icon.Lock size={lockGlyphSize} />
        </span>
      ) : glyph ? (
        <span
          data-testid={`achievement-art-${keyName}`}
          aria-hidden="true"
          className="tnum text-disc leading-none font-extrabold"
        >
          {glyph}
        </span>
      ) : ArtForKey ? (
        <span
          data-testid={`achievement-art-${keyName}`}
          aria-hidden="true"
        >
          <ArtForKey.Icon size={innerSize} />
        </span>
      ) : (
        // Unknown key fallback: render a neutral Sparkle so the tile
        // never collapses to empty + future achievements ship safely
        // even before this mapping is updated.
        <span
          data-testid={`achievement-art-${keyName}`}
          aria-hidden="true"
        >
          <Icon.Sparkle size={innerSize} />
        </span>
      )}
    </div>
  );
}
